import axios from 'axios';
import _snakeCase from 'lodash/snakeCase';
import { titleCase } from 'Services/utils';
const CREPO_API_URL = process.env.REACT_APP_CREPO_API_URL;
const CREPO_API_KEY = process.env.REACT_APP_CREPO_API_KEY;

const CREPO_REQ_HEADERS = {
  headers: {
    'API-Key': CREPO_API_KEY,
  },
};

const RESOURCE_TYPE_MAP = {
  CLIP: 'VIDEO',
  EVENT: 'EVENT_SERIES',
  FILM: 'FILM',
  MIXED: 'MIXED',
  SHOW: 'SHOW',
  STOP: 'EVENT_PROFILE',
  STORY: 'STORY',
};

const TYPE_FIELDS = {
  STORY: 'author',
  EVENT_SERIES: '',
  EVENT_PROFILE: `
    status
    statusMessage
    liveStatus
    startDate {
      dateTimeUTC
    }
    endDate {
      dateTimeUTC
    }
  `,
  MIXED: '',
  SHOW: `
    seasons {
      id
    }
    episodes {
      id
    }
  `,
  FILM: 'videoDurationSeconds',
  VIDEO: 'videoDurationSeconds',
};

const descriptionFragment = (type) => {
  // Story type doesn't contain a description
  if (type === 'STORY' || type === 'MIXED') return '';
  return `
    fragment DescriptionFragment on ${titleCase(type)} {
      description {
        contents {
          ...on AttributeText {
            text
          }
        }
      }
    }
  `;
};

const typeQueryAttrs = (type) => {
  // Story type doesn't contain a description
  if (type === 'MIXED') return ``;
  return `
    ...on ${titleCase(type)} {
      ${type === 'STORY' ? '' : '...DescriptionFragment'}
      ${TYPE_FIELDS[type]}
    }
  `;
};

const resourceQuery = (type) => {
  // Aspect ratio flips based on resource type, see RBMN-27982
  const imageSrcQuery = () => {
    const height = type === 'VIDEO' ? '300' : '450';
    const aspectRatio = type === 'VIDEO' ? 3 / 2 : 2 / 3;
    return `mode: scale, height: ${height}, aspectRatio: ${aspectRatio}`;
  };

  return `
    id
    __typename
    title {
      text
    }
    subHeading {
      text
    }
    teaser {
      text
    }
    url {
      href
    }
    tags(first: 1) {
      title {
        text
      }
    }
    featuredMedia(first: 1) {
      ...on Image {
        imageSrc(${imageSrcQuery()})
      }
    }
    geoBlockedIn
    ${typeQueryAttrs(type)}
  `;
};

const queryResources = ({ ids, type, locale }) => {
  const uppercaseType = type.toUpperCase();
  return `{
    resources(
      ids: [${ids}]
		  includeDraft: false
      includeUnpublished: false
      localeMixing: ["${locale}", "${locale.split('-')[0]}", "en-INT", "en"]
      enforceGeoBlocking: false
    ) {
      ${resourceQuery(uppercaseType)}
    }
    }
    ${descriptionFragment(uppercaseType)}
  `;
};

const resolveResource = ({
  id,
  __typename,
  title,
  subHeading,
  teaser,
  description,
  url,
  tags,
  featuredMedia,
  geoBlockedIn,
  seasons,
  episodes,
  videoDurationSeconds,
  author,
}) => ({
  id,
  type: (__typename && _snakeCase(__typename).toUpperCase()) || '',
  title: title?.text ?? '',
  subHeading: subHeading?.text ?? '',
  teaser: teaser?.text ?? '',
  description: description?.[0]?.contents?.[0]?.text ?? '',
  url: url?.href ?? '',
  tag: tags?.[0]?.title?.text ?? '',
  imageSrc: featuredMedia?.[0]?.imageSrc ?? '',
  geoBlockedIn: geoBlockedIn || [],
  seasonCount: Array.isArray(seasons) && seasons.length,
  episodeCount: Array.isArray(episodes) && episodes.length,
  videoDurationSeconds,
  author: author ?? '',
});

const getHydratedContent = async ({ cancelToken, locale, ids, type }) => {
  const { data } = await axios.post(
    `${CREPO_API_URL}/v1/graphql`,
    {
      query: `${queryResources({
        ids,
        type: RESOURCE_TYPE_MAP[type],
        locale,
      })}`,
    },
    {
      cancelToken,
      ...CREPO_REQ_HEADERS,
    }
  );
  const { resources } = data.data;
  return resources.map(resolveResource);
};

export { getHydratedContent, resolveResource, queryResources };
